/**
 * The navigation menu on a phone (SAM-944, canvas PhoneMenu): who is signed in at the top, the menu entries
 * (the Sidebar in its drawer variant) in the middle and «Log out» at the foot. Tailwind + tokens (SAM-175).
 */
import React from 'react';
import { LogOut } from 'lucide-react';
import { useHeaderTitle } from '../../hooks/useHeaderTitle';
import { useUserPermissions } from '../../hooks/useUserPermissions';
import { useSignedInName } from '../../hooks/useSignedInName';
import { useT } from '../../i18n/admin';

/** Presentational: `name` and `sub` («Admin · Api Beach») come from the wired version below. */
export const PhoneMenuView: React.FC<{ name: string; sub?: string; onLogOut: () => void; children: React.ReactNode }> = ({ name, sub, onLogOut, children }) => {
  const { t } = useT();
  return (
    <div className="flex h-full min-h-0 w-full flex-col bg-white">
      <div className="flex shrink-0 items-center gap-3 border-0 border-b border-solid border-[var(--line)] p-4">
        <span aria-hidden className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-[var(--tenant-primary,#005488)] text-[15px] font-bold text-white">
          {(name || t('header.userInitial')).charAt(0).toUpperCase()}
        </span>
        <div className="min-w-0">
          <div className="truncate text-base font-bold text-[var(--ink-900)]">{name || t('header.user')}</div>
          {sub && <div className="truncate text-xs text-[var(--ink-500)]">{sub}</div>}
        </div>
      </div>
      <div className="flex min-h-0 flex-1 flex-col">{children}</div>
      <button
        type="button"
        onClick={onLogOut}
        className="flex h-14 shrink-0 cursor-pointer items-center gap-3 border-0 border-t border-solid border-[var(--line)] bg-white px-4 text-left font-[inherit] text-[15px] font-semibold text-[var(--ink-900)]"
      >
        <LogOut size={20} strokeWidth={1.8} aria-hidden />
        {t('menu.logOut')}
      </button>
    </div>
  );
};

/** The menu panel wired to the session: the user's first name, their role label and the place (business, estate group or tenant). */
export const PhoneMenuPanel: React.FC<{ onLogOut: () => void; children: React.ReactNode }> = ({ onLogOut, children }) => {
  const { t } = useT();
  const place = useHeaderTitle('');
  const perms = useUserPermissions();
  const name = useSignedInName();
  // SAM-1312 (U38): xhinola_admin counts as isAdmin but is the platform's role, not a tenant admin.
  const role = perms.isXhinolaAdmin
    ? t('menu.roleXhinola')
    : perms.isAdmin
    ? t('menu.roleAdmin')
    : perms.isTenantFrontDesk
      ? t('menu.roleFrontDesk')
      // Through the business (SAM-1321) the role is the business one: Owner / Admin or Operator, not «Estate group ...».
      : perms.isEstateGroupAdmin
        ? t(perms.estateGroupViaBusiness ? 'menu.roleBusinessAdmin' : 'menu.roleEstateGroupAdmin')
        : perms.isEstateGroupFrontDesk
          ? t(perms.estateGroupViaBusiness ? 'menu.roleBusinessOperator' : 'menu.roleEstateGroupFrontDesk')
          : '';
  return <PhoneMenuView name={name} sub={[role, place].filter(Boolean).join(' · ')} onLogOut={onLogOut}>{children}</PhoneMenuView>;
};
